(() => {
  'use strict';

  class HeaderStickyController {
    getPageScrollY() {
      return Math.max(0, window.Axiom?.getPageScrollY?.() ?? window.scrollY ?? 0);
    }

    constructor(header) {
      this.header = header;
      this.headerSection = header.closest('.header-section-shell');
      this.stickyMode = header.dataset.sticky || 'none';
      this.compactEnabled = header.classList.contains('nav--sticky-scroll');
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.lastScrollY = this.getPageScrollY();
      this.lastCompactState = null;
      this.lastAtPageTop = null;
      this.lastMobileStatic = null;
      this.lastHidden = null;
      this.lastTopOffset = null;
      this.lastOverlayTop = null;
      this.flowOffset = 0;
      this.needsFlowRefresh = false;
      this.lastMenuBelowCollapsed = null;
      const configuredHeightProperty = this.desktopQuery.matches
        ? '--header-height-total'
        : '--header-height-mobile';
      this.lastCommittedHeaderHeight = Number.parseFloat(
        header.style.getPropertyValue(configuredHeightProperty),
      ) || null;
      this.frame = 0;
      this.destroyed = false;
      this.resizeObserver = null;
      this.observedPromotion = null;
      this.abortController = new AbortController();

      this.handleScroll = this.scheduleSync.bind(this);
      this.handleViewportChange = () => {
        this.refreshFlowOffset();
        this.sync();
      };
      this.handleStructureChange = () => this.scheduleSync(true);
      this.observeHeight();
      this.refreshFlowOffset();
      window.addEventListener('scroll', this.handleScroll, { passive: true, signal: this.abortController.signal });
      window.addEventListener('resize', this.handleViewportChange, { passive: true, signal: this.abortController.signal });
      this.desktopQuery.addEventListener?.('change', this.handleViewportChange, { signal: this.abortController.signal });
      document.addEventListener('shopify:section:load', this.handleStructureChange, { signal: this.abortController.signal });
      document.addEventListener('shopify:section:unload', this.handleStructureChange, { signal: this.abortController.signal });
      document.addEventListener('shopify:section:reorder', this.handleStructureChange, { signal: this.abortController.signal });
      this.sync();
    }

    observeHeight() {
      const commitHeight = (height) => {
        const configuredHeightProperty = this.desktopQuery.matches
          ? '--header-height-total'
          : '--header-height-mobile';
        const configuredHeight = Number.parseFloat(
          this.header.style.getPropertyValue(configuredHeightProperty),
        );
        const measuredHeight = Math.max(0, Math.round(height || this.header.getBoundingClientRect().height));
        const keepsConfiguredDesktopHeight = this.desktopQuery.matches
          && configuredHeight > 0
          && (this.compactEnabled || this.header.classList.contains('nav--layout-logo_center_menu_below'));
        const nextHeight = keepsConfiguredDesktopHeight
          ? Math.round(configuredHeight)
          : measuredHeight;

        if (!nextHeight || nextHeight === this.lastCommittedHeaderHeight) return;
        this.lastCommittedHeaderHeight = nextHeight;
        document.documentElement.style.setProperty('--axiom-header-height', `${nextHeight}px`);
      };

      if (typeof window.ResizeObserver !== 'function') {
        commitHeight();
        return;
      }

      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries.find((item) => item.target === this.header);
        if (entry) {
          const box = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
          commitHeight(box?.blockSize || entry.contentRect?.height);
          if (document.getElementById('site-overlay')?.classList.contains('overlay--mega-menu-active')) {
            this.syncOverlayTop();
          }
        }
        if (entries.some((item) => item.target === this.observedPromotion)) this.scheduleSync(true);
      });
      this.resizeObserver.observe(this.header);
    }

    syncOverlayTop() {
      const overlayTop = Math.round(this.header.getBoundingClientRect().bottom);
      if (overlayTop === this.lastOverlayTop) return;
      document.documentElement.style.setProperty('--header-overlay-top', `${overlayTop}px`);
      this.lastOverlayTop = overlayTop;
    }

    syncPanelOffset(panel) {
      const offset = `${Math.max(0, this.flowOffset - this.getPageScrollY())}px`;
      panel?.style.setProperty('--header-top-offset', offset);
    }

    observePromotion() {
      if (!this.resizeObserver) return;
      const previous = this.headerSection?.previousElementSibling;
      const promotion = previous?.classList.contains('section-promotion') ? previous : null;
      if (promotion === this.observedPromotion) return;
      if (this.observedPromotion) this.resizeObserver.unobserve(this.observedPromotion);
      this.observedPromotion = promotion;
      if (promotion) this.resizeObserver.observe(promotion);
    }

    refreshFlowOffset() {
      this.observePromotion();
      // Header-group sections before the nav occupy normal flow; measure only when that flow changes.
      this.flowOffset = Math.max(0, Math.round(
        (this.headerSection?.getBoundingClientRect().top || 0) + this.getPageScrollY(),
      ));
    }

    scheduleSync(refreshFlow = false) {
      if (refreshFlow === true) this.needsFlowRefresh = true;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.needsFlowRefresh) {
          this.needsFlowRefresh = false;
          this.refreshFlowOffset();
        }
        this.sync();
      });
    }

    sync() {
      const scrollY = this.getPageScrollY();
      const topOffset = Math.max(0, this.flowOffset - scrollY);
      if (topOffset !== this.lastTopOffset) {
        if (this.stickyMode !== 'none') {
          this.header.style.setProperty('--header-top-offset', `${topOffset}px`);
        }
        this.lastTopOffset = topOffset;
      }
      const menuOpen = this.header.classList.contains('nav--menu-open');
      const compactBlocked = this.header.classList.contains('nav--compact-blocked');
      const canCompact = this.compactEnabled && this.desktopQuery.matches;
      const compactThreshold = this.lastCompactState ? 48 : 64;
      const compact = canCompact && !compactBlocked && scrollY > compactThreshold;
      const atPageTop = scrollY <= 1;
      const mobileStatic = this.compactEnabled && !canCompact;
      const menuBelowLogo = this.header.classList.contains('nav--layout-logo_center_menu_below');
      const menuBelowThreshold = this.lastMenuBelowCollapsed ? 48 : 64;
      const menuBelowCollapsed = this.desktopQuery.matches
        && menuBelowLogo
        && !compactBlocked
        && (compact || (this.stickyMode === 'always' && scrollY > menuBelowThreshold));

      if (atPageTop !== this.lastAtPageTop) {
        this.header.classList.toggle('is-at-page-top', atPageTop);
        this.lastAtPageTop = atPageTop;
      }
      if (mobileStatic !== this.lastMobileStatic) {
        this.header.classList.toggle('nav--sticky-scroll-mobile-static', mobileStatic);
        this.lastMobileStatic = mobileStatic;
      }
      if (compact !== this.lastCompactState) {
        this.header.classList.toggle('scrolled', compact);
        this.lastCompactState = compact;
      }
      if (menuBelowCollapsed !== this.lastMenuBelowCollapsed) {
        this.header.classList.toggle('nav--menu-below-collapsed', menuBelowCollapsed);
        this.lastMenuBelowCollapsed = menuBelowCollapsed;
      }

      const logo = menuBelowLogo ? this.header.querySelector('.nav-logo') : null;
      if (logo) logo.toggleAttribute('inert', menuBelowCollapsed);

      const hidden = this.stickyMode === 'on-scroll-up'
        && scrollY > 60
        && scrollY > this.lastScrollY
        && !menuOpen;
      if (hidden !== this.lastHidden) {
        this.header.classList.toggle('nav--scroll-hidden', hidden);
        this.lastHidden = hidden;
      }

      this.lastScrollY = scrollY;
    }

    destroy() {
      this.destroyed = true;
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.header.classList.remove('nav--scroll-hidden');
      this.header.classList.remove('nav--menu-below-collapsed');
      this.header.querySelector('.nav-logo')?.removeAttribute('inert');
    }
  }

  window.Axiom = window.Axiom || {};
  window.Axiom.mountHeaderSticky = (header) => {
    if (!header) return null;
    header._axiomStickyController?.destroy();
    header._axiomStickyController = new HeaderStickyController(header);
    return header._axiomStickyController;
  };
})();
